import { useState } from "react";
import { TrendingUp, TrendingDown, Database, Zap } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  fmtPrice,
  fmtPct,
  fmtSignedNum,
  fmtVolume,
  pickTrendColor,
} from "../lib/format";
import { useHistory, useQuote } from "../hooks";
import { PriceChart } from "./PriceChart";

const RANGES = [
  { key: "30d", days: 30, label: "30D" },
  { key: "90d", days: 90, label: "90D" },
  { key: "250d", days: 250, label: "250D" },
  { key: "1y", days: 365, label: "1Y" },
  { key: "5y", days: 1825, label: "5Y" },
];

export function TickerDetailDrawer({ ticker, open, onOpenChange }) {
  const [range, setRange] = useState("250d");
  const days = RANGES.find((r) => r.key === range)?.days ?? 250;

  const { quote, error: quoteErr, loading: quoteLoading } = useQuote(
    ticker,
    open,
  );
  const { history, error: historyErr, loading: historyLoading } = useHistory(
    ticker,
    days,
    open,
  );

  if (!ticker) return null;

  const data = quote?.data;
  const chg = data?.change ?? ticker.change;
  const chgPct = data?.change_percent ?? ticker.change_percent;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="ticker-drawer"
        side="right"
        className="w-full sm:max-w-2xl bg-[var(--bg)] border-l border-soft p-0 rounded-none overflow-y-auto"
      >
        <DrawerSymbolHeader
          ticker={ticker}
          data={data}
          chg={chg}
          chgPct={chgPct}
          quote={quote}
          quoteErr={quoteErr}
          quoteLoading={quoteLoading}
        />

        <KpiGrid data={data} ticker={ticker} loading={quoteLoading} />

        <ChartSection
          range={range}
          setRange={setRange}
          history={history}
          historyErr={historyErr}
          historyLoading={historyLoading}
        />
      </SheetContent>
    </Sheet>
  );
}

// ---- Sub-components ------------------------------------------------------

function DrawerSymbolHeader({
  ticker,
  data,
  chg,
  chgPct,
  quote,
  quoteErr,
  quoteLoading,
}) {
  const trendColor = pickTrendColor(chg);
  const TrendIcon = chg > 0 ? TrendingUp : chg < 0 ? TrendingDown : null;

  return (
    <SheetHeader className="px-6 pt-6 pb-5 border-b border-soft text-left space-y-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-soft">
            {ticker.rank ? `Rank #${ticker.rank}` : "Component"}
            {ticker.weight != null
              ? ` · Weight ${ticker.weight.toFixed(2)}%`
              : ""}
          </p>
          <SheetTitle
            data-testid="drawer-symbol"
            className="num font-black tracking-tighter text-4xl text-[var(--text-primary)] mt-2 leading-none"
          >
            {ticker.symbol}
          </SheetTitle>
          <p className="text-sm text-muted-soft mt-1 truncate">
            {ticker.company}
          </p>
        </div>

        <div className="text-right min-w-0">
          <p
            data-testid="drawer-price"
            className="num text-3xl font-bold text-[var(--text-primary)] leading-none"
          >
            {fmtPrice(data?.price ?? ticker.price)}
          </p>
          <p
            className={`num text-sm mt-2 inline-flex items-center gap-1.5 ${trendColor}`}
            data-testid="drawer-change"
          >
            {TrendIcon ? <TrendIcon size={14} strokeWidth={1.75} /> : null}
            {fmtSignedNum(chg)}{" "}
            <span className="text-muted-soft">·</span> {fmtPct(chgPct)}
          </p>
        </div>
      </div>

      <ProviderChipRow
        quote={quote}
        quoteErr={quoteErr}
        quoteLoading={quoteLoading}
        latestTradingDay={data?.latest_trading_day}
      />

      {quote?.attempts?.some((a) => a.status !== "success") ? (
        <AttemptsList attempts={quote.attempts} />
      ) : null}

      {quoteErr ? <ErrorBanner err={quoteErr} /> : null}
    </SheetHeader>
  );
}

function ProviderChipRow({ quote, quoteErr, quoteLoading, latestTradingDay }) {
  if (quoteLoading) {
    return (
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <ProviderChip label="Fetching…" />
      </div>
    );
  }
  if (quoteErr) {
    return (
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <ProviderChip label="Quote unavailable" negative />
      </div>
    );
  }
  if (!quote) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 mt-5">
      <ProviderChip
        icon={<Zap size={10} strokeWidth={1.75} />}
        label={`Served by ${quote.provider?.replaceAll("_", " ") || "—"}`}
        accent
      />
      {quote.cached ? (
        <ProviderChip
          icon={<Database size={10} strokeWidth={1.75} />}
          label="From cache"
        />
      ) : (
        <ProviderChip label="Fresh" />
      )}
      {latestTradingDay ? (
        <ProviderChip
          label={`Trading day ${String(latestTradingDay).slice(0, 10)}`}
        />
      ) : null}
    </div>
  );
}

function ChartSection({
  range,
  setRange,
  history,
  historyErr,
  historyLoading,
}) {
  return (
    <section className="px-6 pt-6 pb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-muted-soft">
          Historical close
        </h3>
        <Tabs value={range} onValueChange={setRange}>
          <TabsList
            className="bg-[var(--bg)] border border-soft rounded-none p-0 h-8"
            data-testid="range-tabs"
          >
            {RANGES.map((r) => (
              <TabsTrigger
                key={r.key}
                value={r.key}
                data-testid={`range-tab-${r.key}`}
                className="rounded-none px-3 h-8 font-mono-tight text-[11px] uppercase tracking-[0.2em] text-muted-soft data-[state=active]:bg-transparent data-[state=active]:text-accent-amber data-[state=active]:shadow-none border-l border-soft first:border-l-0"
              >
                {r.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div
        className="h-72 border border-soft bg-surface p-3"
        data-testid="chart-container"
      >
        {historyErr ? (
          <ChartError err={historyErr} />
        ) : (
          <PriceChart
            data={history?.data}
            loading={historyLoading}
            trendUp={computeTrendUp(history?.data)}
          />
        )}
      </div>

      {history?.provider ? <ChartProviderCaption history={history} /> : null}
    </section>
  );
}

// ---- Helpers --------------------------------------------------------------

function ChartProviderCaption({ history }) {
  const isArchive = history.source === "archive" || history.provider === "archive";
  const providerLabel = isArchive
    ? "Permanent archive"
    : (history.provider || "").replaceAll("_", " ");

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <Badge
        variant="outline"
        data-testid="series-source-chip"
        className={`rounded-none border-soft font-mono-tight uppercase tracking-widest text-[10px] px-2 py-1 gap-1.5 ${
          isArchive
            ? "text-accent-amber border-[var(--accent-hex)]"
            : "text-muted-soft"
        }`}
      >
        {isArchive ? (
          <Database size={10} strokeWidth={1.75} />
        ) : (
          <Zap size={10} strokeWidth={1.75} />
        )}
        {isArchive ? "Served from archive" : `Served by ${providerLabel}`}
      </Badge>
      <Badge
        variant="outline"
        className="rounded-none border-soft font-mono-tight uppercase tracking-widest text-[10px] px-2 py-1 text-muted-soft"
      >
        {history.count} rows
      </Badge>
      {isArchive && history.data?.[0]?.date ? (
        <Badge
          variant="outline"
          data-testid="archive-age-chip"
          className="rounded-none border-soft font-mono-tight uppercase tracking-widest text-[10px] px-2 py-1 text-muted-soft"
          title={`Most recent candle: ${history.data[0].date}`}
        >
          Last update {fmtDateAge(history.data[0].date)}
        </Badge>
      ) : null}
      {!isArchive && history.cached ? (
        <Badge
          variant="outline"
          className="rounded-none border-soft font-mono-tight uppercase tracking-widest text-[10px] px-2 py-1 text-muted-soft"
        >
          From cache
        </Badge>
      ) : null}
    </div>
  );
}

function fmtDateAge(dateStr) {
  // dateStr: 'YYYY-MM-DD' (UTC). Returns 'today' / 'Nd ago' / 'Nw ago' / 'Nmo ago'.
  if (!dateStr) return "—";
  const today = new Date();
  const past = new Date(`${dateStr}T00:00:00Z`);
  const diffDays = Math.max(
    0,
    Math.round((today.getTime() - past.getTime()) / 86_400_000),
  );
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.round(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.round(diffDays / 30)}mo ago`;
  return `${Math.round(diffDays / 365)}y ago`;
}

function computeTrendUp(data) {
  if (!Array.isArray(data) || data.length < 2) return true;
  const sorted = [...data]
    .filter((d) => d.close != null)
    .sort((a, b) => (a.date > b.date ? 1 : -1));
  if (sorted.length < 2) return true;
  return sorted[sorted.length - 1].close >= sorted[0].close;
}

function ProviderChip({ icon, label, accent, negative }) {
  return (
    <Badge
      variant="outline"
      className={`rounded-none border-soft font-mono-tight uppercase tracking-widest text-[10px] px-2 py-1 gap-1.5 ${
        accent
          ? "text-accent-amber border-[var(--accent-hex)]"
          : negative
          ? "text-negative border-[var(--negative)]"
          : "text-muted-soft"
      }`}
    >
      {icon}
      {label}
    </Badge>
  );
}

function KpiGrid({ data, ticker, loading }) {
  const cells = [
    { label: "Open", value: fmtPrice(data?.open) },
    { label: "High", value: fmtPrice(data?.high) },
    { label: "Low", value: fmtPrice(data?.low) },
    { label: "Prev Close", value: fmtPrice(data?.previous_close) },
    {
      label: "Volume",
      value: fmtVolume(data?.volume),
    },
    { label: "Currency", value: data?.currency || "USD" },
    {
      label: "Weight",
      value:
        ticker?.weight != null ? `${ticker.weight.toFixed(2)}%` : "—",
    },
    {
      label: "Rank",
      value: ticker?.rank != null ? `#${ticker.rank}` : "—",
    },
  ];
  return (
    <section
      className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-hex)] mx-6 mt-6 border border-soft"
      data-testid="kpi-grid"
    >
      {cells.map((c) => (
        <div key={c.label} className="bg-surface p-4">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-soft">
            {c.label}
          </p>
          <p className="num text-lg text-[var(--text-primary)] mt-2">
            {loading ? (
              <span className="text-muted-soft">—</span>
            ) : (
              c.value
            )}
          </p>
        </div>
      ))}
    </section>
  );
}

function attemptStatusColor(status) {
  if (status === "success") return "text-positive";
  if (status === "skipped") return "text-muted-soft";
  return "text-negative";
}

function AttemptsList({ attempts }) {
  return (
    <div
      className="mt-5 border border-soft bg-surface px-3 py-2 font-mono-tight"
      data-testid="attempts-list"
    >
      <p className="text-[10px] uppercase tracking-[0.3em] text-muted-soft">
        Fallback trace
      </p>
      <ul className="mt-2 space-y-1 text-[11px]">
        {attempts.map((a, i) => (
          <li
            key={`${a.provider}-${a.status}-${i}`}
            className="flex items-start gap-2"
            data-testid={`attempt-${i}`}
          >
            <span className="num text-muted-soft w-6 text-right">
              {i + 1}.
            </span>
            <span className="text-[var(--text-primary)] w-28 truncate">
              {a.provider}
            </span>
            <span
              className={`${attemptStatusColor(a.status)} uppercase tracking-widest`}
            >
              {a.status}
            </span>
            {a.reason ? (
              <span className="text-muted-soft truncate">— {a.reason}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ErrorBanner({ err }) {
  return (
    <div
      className="mt-5 border border-[var(--negative)] bg-negative-soft px-4 py-3 font-mono-tight"
      data-testid="drawer-error"
    >
      <p className="text-[11px] uppercase tracking-[0.3em] text-negative">
        ▲ All providers failed
      </p>
      <p className="text-xs text-[var(--text-primary)] mt-1 break-all">
        {err.message}
      </p>
      {err.attempts?.length ? <AttemptsList attempts={err.attempts} /> : null}
    </div>
  );
}

function ChartError({ err }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center text-center px-6 font-mono-tight">
      <p className="text-[11px] uppercase tracking-[0.3em] text-negative">
        ▲ Historical unavailable
      </p>
      <p className="text-xs text-muted-soft mt-2 max-w-sm break-all">
        {err.message}
      </p>
    </div>
  );
}
